import type { ComponentProps, CSSProperties } from 'react';
import * as Lucide from 'lucide-react';
import { sxToStyle, type SxObject } from './utils';

type IconProps = ComponentProps<'svg'> & {
    fontSize?: 'small' | 'inherit' | 'large';
    sxProp?: SxObject;
    color?: string;
};

/** 圖示單獨當按鈕內容時，IconButton 用這個語系鍵（SharedUi.zlf）查出預設的無障礙名稱 */
function withAccessibleName<T extends object>(icon: T, accessibleNameKey: string): T {
    return Object.assign(icon, { accessibleNameKey });
}

function createIcon(...names: string[]) {
    const registry = Lucide as unknown as Record<string, React.ComponentType<any>>;
    const Selected = names.map((name) => registry[name]).find(Boolean) || registry.Circle;

    return function Icon({ fontSize, className, sxProp, color, style, ...rest }: IconProps) {
        const size =
            fontSize === 'small' ? 'h-4 w-4' : fontSize === 'large' ? 'h-7 w-7' : 'h-5 w-5';
        const sxStyle = sxToStyle(sxProp) as CSSProperties;
        // MUI 的 sx fontSize 就是圖示尺寸；SVG 不吃 font-size，要換成寬高才有作用
        const customSize = sxStyle.fontSize;
        const sizeStyle: CSSProperties = customSize
            ? { width: customSize, height: customSize, display: 'inline-block' }
            : {};
        return (
            <Selected
                className={[customSize ? '' : size, className].filter(Boolean).join(' ')}
                style={{ color, ...sxStyle, ...sizeStyle, ...(style || {}) }}
                {...rest}
            />
        );
    };
}

export const AccessTime = createIcon('Clock3', 'Clock');
export const AccountBalance = createIcon('Landmark', 'Building2');
export const Add = createIcon('Plus');
export const AddCircle = createIcon('PlusCircle');
export const AllInbox = createIcon('Inbox');
export const ArrowBack = createIcon('ArrowLeft');
export const Assignment = createIcon('ClipboardList');
export const AttachMoney = createIcon('DollarSign', 'BadgeDollarSign');
export const Build = createIcon('Wrench', 'Hammer');
export const Calculate = createIcon('Calculator');
export const CalendarMonth = createIcon('CalendarDays', 'Calendar');
export const CalendarToday = createIcon('Calendar');
export const Cancel = createIcon('CircleX', 'XCircle');
export const Check = createIcon('Check');
export const CheckCircle = createIcon('CircleCheckBig', 'CheckCircle2', 'CircleCheck');
export const ChevronLeft = createIcon('ChevronLeft');
export const ChevronRight = createIcon('ChevronRight');
export const CleaningServices = createIcon('Sparkles', 'BrushCleaning');
export const Close = withAccessibleName(createIcon('X'), 'close');
export const CloudUpload = createIcon('CloudUpload', 'UploadCloud');
export const Construction = createIcon('Construction', 'Hammer');
export const ContentCopy = withAccessibleName(createIcon('Copy'), 'copy');
export const CreditCard = createIcon('CreditCard');
export const Dashboard = createIcon('LayoutDashboard');
export const Delete = withAccessibleName(createIcon('Trash2'), 'delete');
export const Download = createIcon('Download');
export const DragIndicator = createIcon('GripVertical');
export const DarkMode = createIcon('MoonStar', 'Moon');
export const Edit = withAccessibleName(createIcon('Pencil'), 'edit');
export const Email = createIcon('Mail');
export const Error = createIcon('TriangleAlert', 'AlertCircle');
export const Event = createIcon('CalendarRange', 'CalendarClock', 'Calendar');
export const ExitToApp = createIcon('LogOut');
export const ExpandLess = withAccessibleName(createIcon('ChevronUp'), 'collapse');
export const ExpandMore = withAccessibleName(createIcon('ChevronDown'), 'expand');
export const FilterList = withAccessibleName(createIcon('ListFilter', 'Filter'), 'filter');
export const GolfCourse = createIcon('Flag', 'FlagTriangleRight');
export const Group = createIcon('Users');
export const History = withAccessibleName(createIcon('History'), 'history');
export const Home = createIcon('House', 'Home');
export const Info = createIcon('Info');
export const Inventory = createIcon('Package2', 'Package');
export const Key = createIcon('KeyRound', 'Key');
export const KeyboardArrowDown = createIcon('ChevronDown');
export const KeyboardArrowUp = createIcon('ChevronUp');
export const Label = createIcon('Tag');
export const Language = createIcon('Languages', 'Globe');
export const Link = createIcon('Link2', 'Link');
export const Lock = createIcon('Lock');
export const LockReset = createIcon('KeyRound', 'RotateCcwKey');
export const Logout = createIcon('LogOut');
export const LightMode = createIcon('SunMedium', 'Sun');
export const Menu = createIcon('Menu');
export const MonetizationOn = createIcon('CircleDollarSign', 'BadgeDollarSign', 'Coins');
export const MoreVert = createIcon('EllipsisVertical');
export const Note = createIcon('NotebookPen', 'FileText');
export const Payment = createIcon('CreditCard');
export const Payments = createIcon('WalletCards', 'Wallet');
export const People = createIcon('Users');
export const Person = createIcon('User');
export const PersonAdd = createIcon('UserPlus');
export const PersonOff = createIcon('UserMinus', 'UserX');
export const PersonOutline = createIcon('UserRound', 'User');
export const Phone = createIcon('Phone');
export const ImageOutlined = createIcon('Image', 'ImageIcon');
export const PhotoCamera = createIcon('Camera');
export const PlayArrow = createIcon('Play');
export const PlaylistAdd = createIcon('ListPlus');
export const QrCode2 = createIcon('QrCode');
export const QrCodeScanner = createIcon('ScanQrCode', 'QrCode');
export const Receipt = createIcon('ReceiptText', 'Receipt');
export const Refresh = withAccessibleName(createIcon('RefreshCw'), 'refresh');
export const Remove = createIcon('Minus');
export const RemoveCircle = createIcon('MinusCircle');
export const Restaurant = createIcon('UtensilsCrossed', 'Utensils');
export const Save = createIcon('Save');
export const Schedule = createIcon('CalendarClock', 'Clock3');
export const Search = createIcon('Search');
export const SearchOff = createIcon('SearchX', 'SearchSlash');
export const Security = createIcon('Shield', 'ShieldCheck');
export const Settings = createIcon('Settings');
export const ShoppingCart = createIcon('ShoppingCart');
export const SportsGolf = createIcon('Flag', 'Target');
export const Store = createIcon('Store');
export const SwapHoriz = createIcon('ArrowLeftRight');
export const Sync = createIcon('RefreshCcw', 'RefreshCw');
export const TaskAlt = createIcon('BadgeCheck', 'CircleCheckBig');
export const Timeline = createIcon('ChartSpline', 'ChartLine');
export const Today = createIcon('Calendar1', 'Calendar');
export const TrendingUp = createIcon('TrendingUp');
export const Upload = createIcon('Upload');
export const Verified = createIcon('BadgeCheck');
export const VideoLibrary = createIcon('Video');
export const ViewList = createIcon('List');
export const Visibility = withAccessibleName(createIcon('Eye'), 'view');
export const VisibilityOff = createIcon('EyeOff');
export const Warning = createIcon('TriangleAlert');
export const Work = createIcon('BriefcaseBusiness', 'Briefcase');
export const WorkOutline = createIcon('Badge');
export const Category = createIcon('Tags');
